import type { ReactNode } from "react"
import { cn } from "@/lib/utils"

export type StepCard = {
  /** Stable React key (the step's title key or title). */
  id: string
  title: ReactNode
  body: ReactNode
  /** The step's visual, rendered to fill the card's rounded media box. */
  media: ReactNode
  /** Optional link or note under the step text. */
  extra?: ReactNode
}

// Static class names so Tailwind can see every column count it may need.
const DESKTOP_COLS: Record<number, string> = {
  2: "lg:grid-cols-2",
  3: "lg:grid-cols-3",
  4: "lg:grid-cols-4",
  // Five steps get three per row until there is room for all five.
  5: "lg:grid-cols-3 xl:grid-cols-5",
}

/** Columns per row at `lg` and `xl`, matching `DESKTOP_COLS`. */
function rowLengths(count: number) {
  const lg = count === 5 ? 3 : Math.min(Math.max(count, 2), 4)
  return { md: count === 3 ? 3 : 2, lg, xl: count === 5 ? 5 : lg }
}

/**
 * The hairline from a step's number to the next one runs only between steps
 * on the same row: the last card of each row (and the last step) has none.
 * Static class names, one pair per breakpoint, so Tailwind sees them all.
 */
function railClasses(i: number, count: number) {
  const last = i === count - 1
  const ends = (cols: number) => last || (i + 1) % cols === 0
  const { md, lg, xl } = rowLengths(count)
  return cn(
    last ? "after:hidden" : "after:block",
    ends(md) ? "md:after:hidden" : "md:after:block",
    ends(lg) ? "lg:after:hidden" : "lg:after:block",
    ends(xl) ? "xl:after:hidden" : "xl:after:block"
  )
}

/**
 * Compact "How it works" for the membership-first site: a centred heading over
 * one row of equal step cards (numbered circle on a hairline rail, square
 * rounded photo, title, text). Desktop shows every step side by side, tablet two per row, phones a
 * horizontal snap row with the next card peeking in. No timeline rail and no
 * scroll-jacking, so the whole section reads in about one screen.
 *
 * Presentational only: `TestsStepsTimeline` and `BiomarkerStepsWalkthrough`
 * switch to it on the membership-first site and keep their own layouts
 * elsewhere.
 */
export function StepsCardRow({
  eyebrow,
  heading,
  subtitle,
  steps,
  cta,
  size = "default",
  className,
}: {
  eyebrow?: ReactNode
  heading: ReactNode
  subtitle?: ReactNode
  steps: readonly StepCard[]
  /** Centred under the cards. */
  cta?: ReactNode
  /** `large`: a 1260 column with 32px gaps and 18px body text, for a row
   *  that carries the page (/gift, after Superpower's offer row; Eugene,
   *  28.10). */
  size?: "default" | "large"
  /** Shell classes (background, radius, vertical padding). */
  className?: string
}) {
  return (
    <section data-steps-card-row className={cn("py-14 md:py-20", className)}>
      <div className="mx-auto w-full max-w-[720px] px-4 text-center md:px-10">
        {eyebrow && (
          <p className="type-label-lg mb-3 text-neutral-500 uppercase">
            {eyebrow}
          </p>
        )}
        <h2 className="type-display-md-emphasized text-neutral-900">
          {heading}
        </h2>
        {subtitle && (
          <p className="type-body-lg mt-3 text-neutral-500">{subtitle}</p>
        )}
      </div>

      <ol
        className={cn(
          "mx-auto mt-10 w-full md:mt-12",
          // Three steps sit narrower than the page's content, so the
          // squares stay modest (Eugene, 07.10); more steps get the width.
          size === "large"
            ? "max-w-[1260px]"
            : steps.length <= 3
              ? "max-w-[1040px]"
              : "max-w-[1320px]",
          // Phone: swipe row, cards at ~80% so the next one peeks in.
          "flex snap-x snap-mandatory scroll-px-4 [scrollbar-width:none] gap-5 overflow-x-auto px-4 [&::-webkit-scrollbar]:hidden",
          // Tablet: two columns, or all three in a row so the third step is
          // not left alone; desktop: every step in one row.
          "md:grid md:snap-none md:gap-y-12 md:overflow-visible md:px-10",
          steps.length === 3
            ? "md:grid-cols-3 md:gap-x-6 lg:gap-x-12"
            : "md:grid-cols-2 md:gap-x-12",
          DESKTOP_COLS[steps.length] ?? "lg:grid-cols-3",
          size === "large" && "lg:gap-x-8"
        )}
      >
        {steps.map((s, i) => (
          <li key={s.id} className="w-[80%] shrink-0 snap-start md:w-auto">
            {/* The number sits centred above the card in the old timeline's
                circle, and a very light hairline runs on to the next step's
                circle (Eugene, 07.10). Circle centres are one card plus one
                gap apart, so the rail starts 26px right of this centre and is
                that distance minus 52px long. */}
            <div
              className={cn(
                "relative mb-4 flex h-9 justify-center",
                "after:absolute after:top-1/2 after:left-[calc(50%+26px)] after:h-px after:w-[calc(100%-32px)] after:bg-neutral-200 md:after:w-[calc(100%-4px)]",
                // Three steps sit 24px apart on a tablet.
                steps.length === 3 &&
                  "md:after:w-[calc(100%-28px)] lg:after:w-[calc(100%-4px)]",
                // 32px gaps on desktop for the large row.
                size === "large" && "lg:after:w-[calc(100%-20px)]",
                railClasses(i, steps.length)
              )}
            >
              <span
                aria-hidden
                className="type-title-sm flex size-9 items-center justify-center rounded-full bg-white font-medium text-neutral-900 shadow-[inset_0_0_0_2px_rgba(0,0,0,0.2)]"
              >
                {i + 1}
              </span>
            </div>
            <div
              className={cn(
                "relative aspect-square w-full overflow-hidden bg-neutral-100",
                size === "large" ? "rounded-[24px]" : "rounded-[20px]"
              )}
            >
              {s.media}
            </div>
            <h3
              className={cn(
                "text-neutral-900",
                size === "large" ? "mt-6" : "mt-5",
                "type-headline-sm"
              )}
            >
              {s.title}
            </h3>
            <p
              className={cn(
                "mt-2 text-neutral-500",
                size === "large" ? "type-body-xl" : "type-body-lg"
              )}
            >
              {s.body}
            </p>
            {s.extra}
          </li>
        ))}
      </ol>

      {cta && (
        <div className="mt-10 flex justify-center px-4 md:mt-12 md:px-10">
          {cta}
        </div>
      )}
    </section>
  )
}
